import React, { useState } from 'react';
import { Maximize, ExternalLink, Loader2 } from 'lucide-react';

interface VirtualTourEmbedProps {
  url: string;
  title?: string;
  className?: string;
  fullscreenButton?: boolean;
  showExternalLink?: boolean;
  aspectRatio?: '16:9' | '4:3' | '1:1';
}

const VirtualTourEmbed: React.FC<VirtualTourEmbedProps> = ({
  url,
  title = 'Tour Virtual 360°',
  className = '',
  fullscreenButton = true,
  showExternalLink = true,
  aspectRatio = '16:9'
}) => {
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);

  // Detecta o provedor e formata a URL para embed
  const getEmbedUrl = (originalUrl: string): string => {
    if (!originalUrl) return '';
    
    const url = originalUrl.trim();
    
    // Matterport: https://my.matterport.com/show/?m=ABC123
    if (url.includes('matterport.com')) {
      if (url.includes('/show/')) {
        return url.includes('?') ? `${url}&play=1` : `${url}?play=1`;
      }
      // Converter link de compartilhamento para embed
      const matterportMatch = url.match(/matterport\.com\/(?:show\/\?m=|model\/)?([a-zA-Z0-9]+)/);
      if (matterportMatch) {
        return `https://my.matterport.com/show/?m=${matterportMatch[1]}&play=1`;
      }
      return url;
    }
    
    // Kuula: https://kuula.co/share/...
    if (url.includes('kuula.co')) {
      // Kuula já usa iframe embedável
      return url;
    }
    
    // YouTube 360: https://www.youtube.com/watch?v=...
    if (url.includes('youtube.com') || url.includes('youtu.be')) {
      const videoId = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/)|youtu\.be\/)([a-zA-Z0-9_-]+)/);
      if (videoId) {
        return `https://www.youtube.com/embed/${videoId[1]}?rel=0`;
      }
    }
    
    // Cloudpano: https://app.cloudpano.com/tours/...
    if (url.includes('cloudpano.com')) {
      return url;
    }
    
    // iStaging: https://www.istaging.com/...
    if (url.includes('istaging.com')) {
      return url;
    }

    // Pano2VR / panoskin
    if (url.includes('panoskin.com') || url.includes('pano2vr')) {
      return url;
    }
    
    // Default: retorna URL original
    return url;
  };

  // Detecta o provedor para exibir badge
  const getProvider = (url: string): string | null => {
    if (url.includes('matterport.com')) return 'Matterport';
    if (url.includes('kuula.co')) return 'Kuula';
    if (url.includes('youtube.com') || url.includes('youtu.be')) return 'YouTube 360';
    if (url.includes('cloudpano.com')) return 'CloudPano';
    if (url.includes('istaging.com')) return 'iStaging';
    return null;
  };

  const embedUrl = getEmbedUrl(url);
  const provider = getProvider(url);

  const aspectRatioClass = {
    '16:9': 'pb-[56.25%]',
    '4:3': 'pb-[75%]',
    '1:1': 'pb-[100%]'
  }[aspectRatio];

  const handleFullscreen = () => {
    const iframe = document.querySelector('.virtual-tour-iframe') as HTMLIFrameElement;
    if (iframe) {
      if (iframe.requestFullscreen) {
        iframe.requestFullscreen();
      }
    }
  };

  if (!url || !embedUrl) {
    return null;
  }

  return (
    <div className={`relative bg-gray-900 rounded-xl overflow-hidden ${className}`}>
      {/* Header */}
      <div className="absolute top-0 left-0 right-0 z-10 bg-gradient-to-b from-black/60 to-transparent p-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="text-white font-medium text-sm">{title}</span>
          {provider && (
            <span className="bg-white/20 backdrop-blur-sm text-white text-xs px-2 py-0.5 rounded-full">
              {provider}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {showExternalLink && (
            <a
              href={url}
              target="_blank"
              rel="noopener noreferrer"
              className="bg-white/20 backdrop-blur-sm text-white p-2 rounded-lg hover:bg-white/30 transition-colors"
              title="Abrir em nova aba"
            >
              <ExternalLink className="w-4 h-4" />
            </a>
          )}
          {fullscreenButton && (
            <button
              onClick={handleFullscreen}
              className="bg-white/20 backdrop-blur-sm text-white p-2 rounded-lg hover:bg-white/30 transition-colors"
              title="Tela cheia"
            >
              <Maximize className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>

      {/* Loading Skeleton */}
      {isLoading && (
        <div className={`absolute inset-0 flex items-center justify-center bg-gray-800`}>
          <div className="text-center">
            <Loader2 className="w-8 h-8 text-orange-500 animate-spin mx-auto mb-2" />
            <span className="text-white/70 text-sm">Carregando tour virtual...</span>
          </div>
        </div>
      )}

      {/* Error State */}
      {hasError && (
        <div className={`absolute inset-0 flex items-center justify-center bg-gray-800`}>
          <div className="text-center p-4">
            <p className="text-white/70 text-sm mb-3">Não foi possível carregar o tour virtual</p>
            <a
              href={url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-orange-500 text-white px-4 py-2 rounded-lg hover:bg-orange-600 transition-colors"
            >
              <ExternalLink className="w-4 h-4" />
              Abrir externamente
            </a>
          </div>
        </div>
      )}

      {/* Iframe Container */}
      <div className={`relative w-full ${aspectRatioClass}`}>
        <iframe
          src={embedUrl}
          title={title}
          className="virtual-tour-iframe absolute inset-0 w-full h-full border-0"
          allowFullScreen
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; xr-spatial-tracking; vr"
          onLoad={() => setIsLoading(false)}
          onError={() => {
            setIsLoading(false);
            setHasError(true);
          }}
        />
      </div>
    </div>
  );
};

export default VirtualTourEmbed;
